import React from "react";

import { useFeatureOn } from "@services/useFeatureOn";

import { BrazeOperatorProvider } from "./BrazeOperatorProvider";
import type { BrazeOperatorProviderWithFeatureFlipperProps } from "./types";

/**
 * Provider component that enables Operator to make changes to components agentically based on a feature flipper.
 *
 * Operator can make changes to components agentically when:
 * 1. The developer has access to the Operator feature (via `useCanUseOperator`)
 * 2. The specified feature flipper is enabled
 *
 * @example
 * // Enable Operator to make changes to components agentically for SMS composers when the feature is on
 * <BrazeOperatorProviderWithFeatureFlipper feature="sms_operator_mode">
 *   <NameDescription />
 * </BrazeOperatorProviderWithFeatureFlipper>
 */
export const BrazeOperatorProviderWithFeatureFlipper = ({
  children,
  feature,
  suppressDescendantProviders,
  registerToolOverride,
}: BrazeOperatorProviderWithFeatureFlipperProps) => {
  const featureOn = useFeatureOn();

  return (
    <BrazeOperatorProvider
      enabled={featureOn(feature)}
      featureName={feature}
      suppressDescendantProviders={suppressDescendantProviders}
      registerToolOverride={registerToolOverride}
    >
      {children}
    </BrazeOperatorProvider>
  );
};

BrazeOperatorProviderWithFeatureFlipper.displayName = "BrazeOperatorProviderWithFeatureFlipper";
